import * as React from "react";
import { useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import { toast } from "react-toastify";

import * as Content from "../../../services/api/Content";
import { FlagCsvResult, parseFlagCsv } from "./flagCsv";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onImported: () => void;
}

/**
 * Bulk flag backfill (the Rukham history). The file is parsed here so problems
 * are visible before anything is written — an import that half-lands with no
 * explanation is worse than one that refuses to start.
 *
 * The accepted shape is not frozen: tech doc Q6 waits on a sample of the real
 * export, so the parser is permissive about column naming and the modal states
 * what it understood.
 */
export const UploadFlagsModal: React.FC<Props> = ({
  isOpen,
  onClose,
  onImported,
}) => {
  const fileInput = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [parsed, setParsed] = useState<FlagCsvResult | null>(null);
  const [importing, setImporting] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const reset = () => {
    setFileName(null);
    setParsed(null);
    setDragOver(false);
  };

  const readFile = (file: File) => {
    setFileName(file.name);
    const reader = new FileReader();
    reader.onerror = () => toast.error("Could not read that file");
    reader.onload = () => setParsed(parseFlagCsv(String(reader.result ?? "")));
    reader.readAsText(file);
  };

  const runImport = async () => {
    if (!parsed || parsed.rows.length === 0) {
      return;
    }
    setImporting(true);
    try {
      const result = await Content.importFlags({ rows: parsed.rows });
      if (result.failed > 0) {
        // Partial success is the expected case for a historical export, so name
        // the count rather than declaring victory.
        toast.warn(
          `Imported ${result.imported}, ${result.failed} failed — see the list`,
        );
        setParsed({
          rows: [],
          errors: result.failures.map(
            (failure) =>
              `${failure.line ? `Line ${failure.line}: ` : ""}${failure.error}`,
          ),
        });
      } else {
        toast.success(`Imported ${result.imported} flags`);
        reset();
        onClose();
      }
      onImported();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setImporting(false);
    }
  };

  const close = () => {
    reset();
    onClose();
  };

  return (
    <Modal isOpen={isOpen} toggle={close} size="lg">
      <ModalHeader toggle={close}>Upload flags (CSV)</ModalHeader>
      <ModalBody>
        <div
          className={`text-center p-5 border rounded ${
            dragOver ? "border-primary bg-light" : ""
          }`}
          style={{ borderStyle: "dashed", cursor: "pointer" }}
          onClick={() => fileInput.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            const file = e.dataTransfer.files?.[0];
            if (file) readFile(file);
          }}
        >
          <i className="fas fa-file-csv fa-2x text-muted mb-2" />
          <div>{fileName ?? "Drop a flags CSV here, or click to browse"}</div>
          <small className="text-muted">
            Needs a header row with <code>reason</code> plus{" "}
            <code>questionId</code> or <code>title</code>; <code>comment</code>{" "}
            and <code>customer</code> are optional.
          </small>
          <input
            ref={fileInput}
            type="file"
            accept=".csv,text/csv"
            className="d-none"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) readFile(file);
            }}
          />
        </div>

        {parsed && (
          <div className="mt-3">
            {parsed.rows.length > 0 && (
              <Alert color="info" className="mb-2">
                <Badge color="info" className="mr-2">
                  {parsed.rows.length}
                </Badge>
                flags ready to import
                <div className="mt-2">
                  {parsed.rows.slice(0, 5).map((row) => (
                    <div key={row.line}>
                      <small>
                        <Badge color="warning" className="mr-1">
                          {row.reason.replace("_", " ")}
                        </Badge>
                        {row.questionId ?? row.questionTitle}
                        {row.customer && (
                          <span className="text-muted"> · {row.customer}</span>
                        )}
                      </small>
                    </div>
                  ))}
                  {parsed.rows.length > 5 && (
                    <small className="text-muted">
                      …and {parsed.rows.length - 5} more
                    </small>
                  )}
                </div>
              </Alert>
            )}
            {parsed.errors.length > 0 && (
              <Alert color="danger" className="mb-0">
                <strong>{parsed.errors.length} row(s) need attention</strong>
                <ul className="mb-0 mt-1">
                  {parsed.errors.slice(0, 10).map((error) => (
                    <li key={error}>
                      <small>{error}</small>
                    </li>
                  ))}
                </ul>
                {parsed.errors.length > 10 && (
                  <small className="text-muted">
                    …and {parsed.errors.length - 10} more
                  </small>
                )}
              </Alert>
            )}
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={close} disabled={importing}>
          Close
        </Button>
        <Button
          color="primary"
          onClick={runImport}
          disabled={importing || !parsed || parsed.rows.length === 0}
        >
          {importing
            ? "Importing…"
            : parsed?.rows.length
            ? `Import ${parsed.rows.length}`
            : "Import"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};
